/**
 * SharedHeatmap (Iter 31) — public unauthenticated read-only viewer for
 * ABAP heat-map share tokens.
 *
 * Renders at /share/heatmap/:token. Calls `GET /api/share/heatmap/:token`
 * (no auth) to fetch the rollup + optional Claude narrative. Surfaces
 * recipient watermark, workspace name, expiry date, view-count notice, and
 * a "Download PDF" button.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FireIcon as Fire,
  WarningOctagonIcon as WarningOctagon,
  ChartBarIcon as ChartBar,
  ClockIcon as Clock,
  DownloadSimpleIcon as Download,
  ShieldCheckIcon as ShieldCheck,
  CircleNotchIcon as Spinner,
} from "@phosphor-icons/react";
import Markdown from "@/components/Markdown";
import { getHeatmapShare, heatmapSharePdfUrl } from "@/lib/api";

const BAND_STYLE = {
  HIGH:   { bg: "#fff1f1", fg: "#a2191f", border: "#ffd7d9", label: "HIGH" },
  MEDIUM: { bg: "#fff8e1", fg: "#8a6c00", border: "#f1c21b", label: "MED" },
  LOW:    { bg: "#defbe6", fg: "#0e6027", border: "#a7f0ba", label: "LOW" },
};

function BandPill({ band }) {
  const s = BAND_STYLE[band] || BAND_STYLE.LOW;
  return (
    <span
      className="ml-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border"
      style={{ background: s.bg, color: s.fg, borderColor: s.border }}
      data-testid={`share-band-${band}`}
    >
      {s.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return iso;
  }
}

function StatCard({ label, value, color, testId }) {
  return (
    <div className="ml-card p-4" data-testid={testId}>
      <div className="ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{label}</div>
      <div className="text-2xl font-semibold mt-1" style={{ color }}>
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

export default function SharedHeatmap() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getHeatmapShare(token)
      .then(setData)
      .catch(() =>
        setError("This share link is invalid, expired, or has been revoked."),
      );
  }, [token]);

  // Lightweight runtime <title> / OG meta update (same pattern as
  // SharedWorkspace.jsx) so the browser tab + share previews look polished.
  useEffect(() => {
    if (!data) return;
    const ws = data.workspace?.name || "Modernization Lab";
    const prev = document.title;
    document.title = `${ws} · ABAP Heat Map`;
    return () => {
      document.title = prev;
    };
  }, [data]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="share-error">
        <WarningOctagon size={48} weight="duotone" className="text-[#a2191f] inline mb-3" />
        <h1 className="text-2xl font-semibold mb-2">Share link not available</h1>
        <p className="text-[var(--ml-text-secondary)]">{error}</p>
        <p className="text-xs text-[var(--ml-text-secondary)] mt-4">
          If you received this URL from a Modernization Lab consultant, ask them to issue a fresh share link.
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="share-loading">
        <Spinner size={32} className="animate-spin text-[var(--ml-text-secondary)] inline mb-3" />
        <p className="text-sm text-[var(--ml-text-secondary)]">Loading heat map…</p>
      </div>
    );
  }

  const { workspace, rollup, narrative, recipient_email, recipient_label, expires_at } = data;
  const { totals, packages, top_modified_standard } = rollup;

  return (
    <div className="min-h-screen bg-white" data-testid="shared-heatmap">
      {/* Brand strip */}
      <div className="bg-[#0f62fe] text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="ml-mono text-[10px] uppercase tracking-widest opacity-80">Modernization Lab</div>
          <div className="text-sm font-semibold">SAP ABAP Heat Map</div>
        </div>
        <a
          href={heatmapSharePdfUrl(token)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-download-pdf"
          className="text-white hover:bg-white/10 px-3 py-1 text-sm flex items-center gap-1.5 border border-white/20"
        >
          <Download size={14} weight="bold" /> Download PDF
        </a>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        {/* Watermark banner */}
        {(recipient_email || recipient_label) && (
          <div
            className="border-l-4 border-[#0f62fe] bg-[#edf5ff] px-4 py-3 text-sm"
            data-testid="share-watermark"
          >
            <div className="flex items-center gap-2 text-[#001d6c]">
              <ShieldCheck size={14} weight="bold" />
              <span>
                <strong>Shared with:</strong>{" "}
                {recipient_email || recipient_label}
                {recipient_email && recipient_label && (
                  <span className="text-[var(--ml-text-secondary)]"> — {recipient_label}</span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Header */}
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <Fire size={11} weight="bold" /> Transition-priority report
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">{workspace.name}</h1>
          {workspace.client_name && (
            <p className="text-[var(--ml-text-secondary)]">{workspace.client_name}</p>
          )}
          <p className="text-xs text-[var(--ml-text-secondary)] mt-1 flex items-center gap-1">
            <Clock size={11} weight="bold" /> Link expires {fmtDate(expires_at)}
          </p>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="share-totals">
          <StatCard label="ABAP Objects" value={totals.objects} testId="share-stat-objects" />
          <StatCard label="Packages" value={totals.packages} testId="share-stat-packages" />
          <StatCard label="HIGH risk pkgs" value={totals.high_risk_packages} color="#a2191f" testId="share-stat-high" />
          <StatCard label="MED risk pkgs" value={totals.medium_risk_packages} color="#8a6c00" testId="share-stat-medium" />
          <StatCard label="LOW risk pkgs" value={totals.low_risk_packages} color="#0e6027" testId="share-stat-low" />
        </div>

        {/* Package table */}
        <div className="ml-card overflow-x-auto" data-testid="share-package-table">
          <table className="w-full text-sm">
            <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
              <tr>
                <th className="px-3 py-2 text-left">Band</th>
                <th className="px-3 py-2 text-left">Package</th>
                <th className="px-3 py-2 text-right">Score</th>
                <th className="px-3 py-2 text-right">Objects</th>
                <th className="px-3 py-2 text-right">Custom / Mod-std</th>
                <th className="px-3 py-2 text-right">LOC</th>
                <th className="px-3 py-2 text-left">Top objects</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((p) => (
                <tr
                  key={p.package}
                  className="border-b border-[var(--ml-border)]"
                  data-testid={`share-row-${p.package.replace(/[/\s()]/g, "_")}`}
                >
                  <td className="px-3 py-2"><BandPill band={p.risk_band} /></td>
                  <td className="px-3 py-2 ml-mono font-semibold">{p.package}</td>
                  <td className="px-3 py-2 ml-mono text-right">{p.risk_score.toLocaleString()}</td>
                  <td className="px-3 py-2 ml-mono text-right">{p.object_count}</td>
                  <td className="px-3 py-2 ml-mono text-right text-xs">
                    {p.custom_count}
                    {p.modified_standard_count > 0 && (
                      <span className="ml-1 text-[#a2191f] font-semibold">
                        · {p.modified_standard_count} mod
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 ml-mono text-right text-xs">{p.total_loc.toLocaleString()}</td>
                  <td className="px-3 py-2 ml-mono text-xs">
                    {p.top_objects.slice(0, 2).map((o) => o.obj_name).join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {top_modified_standard.length > 0 && (
          <div className="ml-card p-4" data-testid="share-mod-standard">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
              <WarningOctagon size={11} weight="bold" /> Modified-standard hot spots ({top_modified_standard.length})
            </div>
            <div className="space-y-1.5">
              {top_modified_standard.map((o) => (
                <div
                  key={`${o.obj_type}_${o.obj_name}`}
                  className="flex flex-wrap items-center gap-x-3 text-sm border-l-2 pl-3 py-1"
                  style={{ borderColor: "#fa4d56" }}
                >
                  <span className="ml-mono font-semibold">{o.obj_type} {o.obj_name}</span>
                  <span className="ml-mono text-xs text-[var(--ml-text-secondary)]">
                    pkg={o.package || "—"} · {(o.loc || 0).toLocaleString()} LOC
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {narrative && (
          <div className="ml-card p-6 mt-8" data-testid="share-narrative">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-3">
              <ChartBar size={11} weight="bold" /> AI-generated transition analysis
            </div>
            <div className="prose prose-sm max-w-none">
              <Markdown text={narrative} />
            </div>
          </div>
        )}

        <div className="text-xs text-[var(--ml-text-secondary)] border-t border-[var(--ml-border)] pt-4 mt-8">
          This is a read-only share link issued by a Modernization Lab consultant.
          The data shown is generated server-side from the customer&apos;s TADIR / REPOSRC
          extracts; risk methodology is described in the bundled PDF.
        </div>
      </div>
    </div>
  );
}
